// client/src/components/configuracoes/horarios/HorarioForm.js (VERSÃO BLINDADA)

import React, { useState, useEffect } from 'react';
import { useMutateHorarios } from '../../../hooks/useHorarios';
import useAlert from '../../../hooks/useAlert';
import { XCircle, PlusCircle, Trash2, Calendar, CalendarDays } from 'lucide-react';
import { FormStepper, FormStepNavigation } from '../../shared/FormStepper';

const STEP_LABELS = ['Dados do Horário'];

const initialState = {
    tipo: 'SEMANAL',
    data: new Date().toISOString().split('T')[0],
    diaDaSemana: '1', 
    horaInicio: '09:00', 
    horaFim: '18:00', 
    intervalos: [], 
    unidade: '' 
};

const diasDaSemana = [
    { num: '1', nome: 'Segunda' }, { num: '2', nome: 'Terça' },
    { num: '3', nome: 'Quarta' }, { num: '4', nome: 'Quinta' },
    { num: '5', nome: 'Sexta' }, { num: '6', nome: 'Sábado' }, { num: '0', nome: 'Domingo' }
];

const HorarioForm = ({ entidadeId, tipoEntidade, onCloseForm, current, clearCurrent, horarios, unidadesDisponiveis }) => { 
    const { setAlert } = useAlert();
    const { createHorario, updateHorario } = useMutateHorarios();

    const [horario, setHorario] = useState(initialState);
    const [diasParaAplicar, setDiasParaAplicar] = useState([]);
    
    const isEditing = !!current;
    const isLoading = createHorario.isPending || updateHorario.isPending;

    useEffect(() => {
        if (current) {
            setHorario({ 
                ...initialState,
                ...current, 
                diaDaSemana: current.diaDaSemana?.toString() || '1',
                data: current.data ? new Date(current.data).toISOString().split('T')[0] : initialState.data,
                unidade: current.unidade?._id || current.unidade || '' 
            });
        } else {
            // Se houver apenas uma unidade disponível, auto-seleciona ela para evitar erros
            const autoUnidade = unidadesDisponiveis?.length === 1 ? (unidadesDisponiveis[0]._id || unidadesDisponiveis[0].id) : '';
            setHorario({ ...initialState, unidade: autoUnidade });
        }
        setDiasParaAplicar([]);
    }, [current, unidadesDisponiveis]);

    const onChange = e => setHorario({ ...horario, [e.target.name]: e.target.value });
    const addIntervalo = () => setHorario({ ...horario, intervalos: [...(horario.intervalos || []), { inicio: '12:00', fim: '13:00' }] });
    const handleIntervaloChange = (index, e) => { const n = [...horario.intervalos]; n[index][e.target.name] = e.target.value; setHorario({ ...horario, intervalos: n }); };
    const removeIntervalo = index => setHorario({ ...horario, intervalos: horario.intervalos.filter((_, i) => i !== index) });
    const handleDiaParaAplicarChange = (dia) => setDiasParaAplicar(prev => prev.includes(dia) ? prev.filter(d => d !== dia) : [...prev, dia]);
    const handleClose = () => { clearCurrent(); onCloseForm(); };

    const onSubmit = async (e) => {
        e.preventDefault();

        if (!tipoEntidade || !['User', 'Recurso', 'Unidade'].includes(tipoEntidade)) {
            return setAlert('Tipo de entidade inválido. Não é possível salvar.', 'danger');
        }

        if (!horario.unidade) { 
            return setAlert('Por favor, selecione uma unidade de atendimento.', 'warning');
        }

        if (horario.horaInicio >= horario.horaFim) {
            return setAlert('O horário de início não pode ser maior ou igual ao horário de fim.', 'warning');
        }

        const payloadBase = {
            tipo: horario.tipo,
            horaInicio: horario.horaInicio,
            horaFim: horario.horaFim,
            intervalos: horario.intervalos || [],
            entidadeAgendavel: entidadeId,
            tipoEntidade: tipoEntidade,
            unidade: horario.unidade, 
        };
        
        if (horario.tipo === 'SEMANAL') {
            delete payloadBase.data;
        } else {
            delete payloadBase.diaDaSemana;
            payloadBase.data = horario.data;
        }

        console.log('🚀 [SUBMIT_HORARIO] Payload formatado pronto para envio:', payloadBase);

        try {
            if (isEditing) {
                const payloadFinal = { ...payloadBase, diaDaSemana: horario.diaDaSemana };
                await updateHorario.mutateAsync({ id: current._id, horarioData: payloadFinal });
            } else {
                const diasACriar = horario.tipo === 'SEMANAL' ? [...new Set([horario.diaDaSemana, ...diasParaAplicar])] : [null];
                console.log(`🚀 [SUBMIT_HORARIO] Criando ${diasACriar.length} regra(s) de horário...`);
                
                const promessas = diasACriar.map(dia => {
                    const payload = { ...payloadBase };
                    if (dia) payload.diaDaSemana = parseInt(dia, 10); // Transforma em Number para o backend não dar 400/404
                    return createHorario.mutateAsync(payload);
                });
                await Promise.all(promessas);
            }
            handleClose();
        } catch (err) {
            console.error("❌ [SUBMIT_HORARIO] Falha ao salvar horários:", err);
            // O erro real já foi emitido pelo setAlert dentro do useHorarios
        }
    };
    
    return (
        <form onSubmit={onSubmit} noValidate>
            <div className="form-header">
                <h3 className="form-title">{isEditing ? 'Editar Horário' : 'Adicionar Novo Horário'}</h3>
                <button type="button" onClick={handleClose} title="Fechar" className="form-close-button"><XCircle size={24} /></button>
            </div>

            <FormStepper currentStep={1} steps={STEP_LABELS} />

            <fieldset className="form-section">
                <legend className="form-section-title">Tipo de Regra</legend>
                <div className="service-category-filters mb-4">
                    <button type="button" className={`category-button ${horario.tipo === 'SEMANAL' ? 'is-active' : ''}`} onClick={() => setHorario(prev => ({...prev, tipo: 'SEMANAL'}))} disabled={isEditing}>
                        <CalendarDays size={16} className="icon" /> Semanal
                    </button>
                    <button type="button" className={`category-button ${horario.tipo === 'DATA_UNICA' ? 'is-active' : ''}`} onClick={() => setHorario(prev => ({...prev, tipo: 'DATA_UNICA'}))} disabled={isEditing}>
                        <Calendar size={16} className="icon" /> Data Única
                    </button>
                </div>
            </fieldset>

            <fieldset className="form-section">
                <legend className="form-section-title">Horário Principal</legend>
                <div className="form-grid-container">
                    <div className="form-group">
                        <label htmlFor="unidade">Unidade de Atendimento*</label>
                        <select name="unidade" id="unidade" value={horario.unidade} onChange={onChange} className="form-input" required>
                            <option value="">-- Selecione --</option>
                            {unidadesDisponiveis?.length > 0 ? (
                                unidadesDisponiveis.map(uni => <option key={uni._id || uni.id} value={uni._id || uni.id}>{uni.nome}</option>)
                            ) : (
                                <option disabled>Nenhuma unidade vinculada para criar horário.</option>
                            )}
                        </select>
                    </div>
                    
                    {horario.tipo === 'SEMANAL' && (
                        <div className="form-group">
                            <label htmlFor="diaDaSemana">Dia da Semana*</label>
                            <select name="diaDaSemana" id="diaDaSemana" value={horario.diaDaSemana} onChange={onChange} className="form-input" disabled={isEditing}>
                                {diasDaSemana.map(d => <option key={d.num} value={d.num}>{d.nome}</option>)}
                            </select>
                        </div>
                    )}

                    {horario.tipo === 'DATA_UNICA' && (
                         <div className="form-group">
                            <label htmlFor="data">Data*</label>
                            <input type="date" name="data" id="data" value={horario.data} onChange={onChange} className="form-input" disabled={isEditing} />
                         </div>
                    )}
                    
                    <div className="form-group">
                        <label htmlFor="horaInicio">Início*</label>
                        <input type="time" id="horaInicio" name="horaInicio" value={horario.horaInicio} onChange={onChange} className="form-input" />
                    </div>
                    <div className="form-group">
                        <label htmlFor="horaFim">Fim*</label>
                        <input type="time" id="horaFim" name="horaFim" value={horario.horaFim} onChange={onChange} className="form-input" />
                    </div>
                </div>
            </fieldset>

            {horario.tipo === 'SEMANAL' && !isEditing && (
                 <fieldset className="form-section">
                    <legend className="form-section-title">Aplicar este horário para outros dias</legend>
                    <p className="form-hint mb-4">Selecione dias para aplicar esta mesma configuração.</p>
                    <div className="checkbox-group-container is-horizontal">
                        {diasDaSemana.map(dia => (
                            <div key={dia.num} className="form-group-checkbox">
                                <input 
                                    type="checkbox" 
                                    id={`dia-${dia.num}`} 
                                    value={dia.num} 
                                    disabled={horario.diaDaSemana === dia.num} 
                                    checked={diasParaAplicar.includes(dia.num)} 
                                    onChange={() => handleDiaParaAplicarChange(dia.num)} 
                                    className="form-checkbox"
                                />
                                <label htmlFor={`dia-${dia.num}`}>{dia.nome}</label>
                            </div>
                        ))}
                    </div>
                </fieldset>
            )}

            <fieldset className="form-section">
                <legend className="form-section-title">Intervalos (Pausas)</legend>
                <div className="dynamic-list-container">
                    {(horario.intervalos || []).map((intervalo, index) => (
                        <div key={index} className="dynamic-list-item">
                            <div className="form-group">
                                <label>Início</label>
                                <input type="time" name="inicio" value={intervalo.inicio} onChange={e => handleIntervaloChange(index, e)} className="form-input" />
                            </div>
                            <div className="form-group">
                                <label>Fim</label>
                                <input type="time" name="fim" value={intervalo.fim} onChange={e => handleIntervaloChange(index, e)} className="form-input" />
                            </div>
                            <button type="button" onClick={() => removeIntervalo(index)} className="remove-item-button">
                                <Trash2 size={16}/>
                            </button>
                        </div>
                    ))}
                    <button type="button" onClick={addIntervalo} className="add-item-button">
                        <PlusCircle size={16} className="icon"/> Adicionar Intervalo
                    </button>
                </div>
            </fieldset>

            <div className="form-actions">
                <button type="button" onClick={handleClose} className="action-button action-button-secondary">
                    <XCircle size={18} className="icon" /> Cancelar
                </button>
                <FormStepNavigation
                    currentStep={1}
                    totalSteps={1}
                    onBack={() => {}}
                    onNext={() => {}}
                    isSubmitting={isLoading}
                    submitLabel={isEditing ? 'Atualizar Horário' : 'Salvar Horários'}
                />
            </div>
        </form>
    );
};

export default HorarioForm;